/**
 * PromptEditorModal - Modal for viewing and editing prompt templates
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as Dialog from '@radix-ui/react-dialog';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import {
  getPromptTemplate,
  createUserPromptTemplate,
  updatePromptTemplate,
  previewPromptTemplate,
  type PromptTemplate,
} from '@services/api/promptTemplates';

interface PromptEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  promptType: string;
  personaName: string;
  templateId?: string;
  onSave: () => void;
}

export default function PromptEditorModal({
  isOpen,
  onClose,
  promptType,
  personaName,
  templateId,
  onSave,
}: PromptEditorModalProps) {
  const { t } = useTranslation();
  const [template, setTemplate] = useState<PromptTemplate | null>(null);
  const [promptText, setPromptText] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [temperature, setTemperature] = useState(0.3);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewText, setPreviewText] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  // Snapshot of the loaded values, used to detect unsaved edits (dirty state)
  const [baseline, setBaseline] = useState({
    promptText: '',
    displayName: '',
    description: '',
    temperature: 0.3,
  });

  // Load template data when modal opens
  useEffect(() => {
    if (!isOpen) return;

    const loadTemplate = async () => {
      setLoading(true);
      setError(null);

      try {
        if (templateId) {
          // Editing existing template
          const data = await getPromptTemplate(templateId);
          setTemplate(data);
          setPromptText(data.prompt_template);
          setDisplayName(data.display_name);
          setDescription(data.description || '');
          setTemperature(data.temperature);
          setBaseline({
            promptText: data.prompt_template,
            displayName: data.display_name,
            description: data.description || '',
            temperature: data.temperature,
          });
        } else {
          // Creating new template - start with defaults
          const defaultDisplayName = `Custom ${personaName}`;
          setTemplate(null);
          setPromptText('');
          setDisplayName(defaultDisplayName);
          setDescription('');
          setTemperature(0.3);
          setBaseline({
            promptText: '',
            displayName: defaultDisplayName,
            description: '',
            temperature: 0.3,
          });
        }
      } catch (err) {
        setError(t('settings.aiPromptsSettings.editor.loadError'));
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    void loadTemplate();
  }, [isOpen, templateId, personaName]);

  const handlePreview = async () => {
    if (!promptText.trim()) return;

    setPreviewLoading(true);
    setError(null);

    try {
      // Extract variables from template (anything in {curly_braces})
      const variableMatches = promptText.match(/\{([^}]+)\}/g) || [];
      const variables: Record<string, string> = {};

      // Provide example values for common variables
      variableMatches.forEach((match) => {
        const varName = match.slice(1, -1);
        variables[varName] = `[Example ${varName}]`;
      });

      const response = await previewPromptTemplate({
        prompt_template: promptText,
        variables,
      });

      setPreviewText(response.rendered_prompt);
      setShowPreview(true);
    } catch (err) {
      setError(t('settings.aiPromptsSettings.editor.previewError'));
      console.error(err);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSave = async () => {
    if (!promptText.trim() || !displayName.trim()) {
      setError(t('settings.aiPromptsSettings.editor.validationError'));
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Extract required variables from template
      const variableMatches = promptText.match(/\{([^}]+)\}/g) || [];
      const requiredVariables = variableMatches.map((match) => match.slice(1, -1));

      if (templateId && template) {
        // Update existing template
        const updateData: Record<string, any> = {
          prompt_template: promptText,
          display_name: displayName,
          temperature,
          required_variables: requiredVariables,
          // Always send description so clearing it actually persists (null clears it)
          description: description.trim() || null,
        };
        await updatePromptTemplate(templateId, updateData as any);
      } else {
        // Create new user-level template
        const createData: Record<string, any> = {
          prompt_type: promptType,
          persona_name: personaName,
          prompt_template: promptText,
          display_name: displayName,
          temperature,
          required_variables: requiredVariables,
        };
        if (description.trim()) {
          createData.description = description.trim();
        }

        await createUserPromptTemplate(createData as any);
      }

      onSave();
      onClose();
    } catch (err) {
      setError(t('settings.aiPromptsSettings.editor.saveError'));
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const isDirty =
    promptText !== baseline.promptText ||
    displayName !== baseline.displayName ||
    description !== baseline.description ||
    temperature !== baseline.temperature;

  // Route every close attempt (X, Cancel, Escape, backdrop) through here so
  // unsaved edits are protected and closing mid-save is blocked.
  const handleCloseRequest = () => {
    if (saving) return;
    if (isDirty) {
      setShowDiscardConfirm(true);
      return;
    }
    onClose();
  };

  const missingRequired = !promptText.trim() || !displayName.trim();

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && handleCloseRequest()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-neutral-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
          <div className="flex items-center justify-between">
            <div>
              <Dialog.Title className="text-xl font-semibold text-neutral-900 dark:text-white">
                {templateId ? t('settings.aiPromptsSettings.editor.editTitle') : t('settings.aiPromptsSettings.editor.createTitle')}
              </Dialog.Title>
              <Dialog.Description className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
                {promptType} - {personaName}
              </Dialog.Description>
            </div>
            <button
              type="button"
              onClick={handleCloseRequest}
              disabled={saving}
              aria-label={t('common.close', 'Close')}
              className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {error && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-neutral-500 dark:text-neutral-400">{t('common.loading')}</div>
            </div>
          ) : (
            <>
              {/* Display Name */}
              <div>
                <label htmlFor="prompt-display-name" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  {t('settings.aiPromptsSettings.editor.displayName')}
                </label>
                <input
                  id="prompt-display-name"
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder={t('settings.aiPromptsSettings.editor.displayNamePlaceholder')}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Description */}
              <div>
                <label htmlFor="prompt-description" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  {t('settings.aiPromptsSettings.editor.description')} <span className="text-neutral-400">({t('common.optional')})</span>
                </label>
                <input
                  id="prompt-description"
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('settings.aiPromptsSettings.editor.descriptionPlaceholder')}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Temperature */}
              <div>
                <label htmlFor="prompt-temperature" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  {t('settings.aiPromptsSettings.editor.temperature')}: {temperature.toFixed(1)}
                </label>
                <input
                  id="prompt-temperature"
                  type="range"
                  min="0"
                  max="2"
                  step="0.1"
                  value={temperature}
                  onChange={(e) => setTemperature(parseFloat(e.target.value))}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                  <span>{t('settings.aiPromptsSettings.editor.temperaturePrecise')}</span>
                  <span>{t('settings.aiPromptsSettings.editor.temperatureBalanced')}</span>
                  <span>{t('settings.aiPromptsSettings.editor.temperatureCreative')}</span>
                </div>
              </div>

              {/* Prompt Template */}
              <div>
                <label htmlFor="prompt-template-text" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  {t('settings.aiPromptsSettings.editor.promptTemplate')}
                </label>
                <textarea
                  id="prompt-template-text"
                  value={promptText}
                  onChange={(e) => setPromptText(e.target.value)}
                  placeholder={t('settings.aiPromptsSettings.editor.promptPlaceholder')}
                  rows={12}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white font-mono text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                  {t('settings.aiPromptsSettings.editor.variableHint')}
                </p>
              </div>

              {/* Preview Button */}
              <div>
                <button
                  onClick={handlePreview}
                  disabled={!promptText.trim() || previewLoading}
                  className="px-4 py-2 bg-neutral-100 text-neutral-700 rounded-lg hover:bg-neutral-200 dark:bg-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {previewLoading ? t('settings.aiPromptsSettings.editor.previewing') : t('settings.aiPromptsSettings.editor.preview')}
                </button>
              </div>

              {/* Preview */}
              {showPreview && previewText && (
                <div className="border border-neutral-300 dark:border-neutral-600 rounded-lg p-4 bg-neutral-50 dark:bg-neutral-900">
                  <h4 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    {t('settings.aiPromptsSettings.editor.previewTitle')}
                  </h4>
                  <div className="text-sm text-neutral-600 dark:text-neutral-400 whitespace-pre-wrap font-mono">
                    {previewText}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-neutral-200 dark:border-neutral-700 flex items-center justify-between gap-3">
          <p className="text-xs text-neutral-500 dark:text-neutral-400 min-h-[1rem]">
            {!loading && missingRequired ? t('settings.aiPromptsSettings.editor.validationError') : ''}
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={handleCloseRequest}
              disabled={saving}
              className="px-4 py-2 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={saving || missingRequired}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? t('settings.aiPromptsSettings.editor.saving') : t('settings.aiPromptsSettings.editor.saveTemplate')}
            </button>
          </div>
        </div>
        </Dialog.Content>
      </Dialog.Portal>

      {/* Discard-changes confirmation */}
      <ConfirmDialog
        open={showDiscardConfirm}
        onClose={() => setShowDiscardConfirm(false)}
        onConfirm={() => {
          setShowDiscardConfirm(false);
          onClose();
        }}
        title={t('settings.aiPromptsSettings.editor.discardTitle', 'Discard changes?')}
        description={t(
          'settings.aiPromptsSettings.editor.discardMessage',
          'You have unsaved changes. If you close now, your edits will be lost.'
        )}
        confirmLabel={t('common.discard', 'Discard')}
        cancelLabel={t('common.cancel')}
        variant="warning"
      />
    </Dialog.Root>
  );
}
